import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { firstValueFrom, filter, take, timeout, catchError, of } from 'rxjs';
import { User } from '@angular/fire/auth';

import { PlatformService } from '@app/core/platform/platform.service';
import { isAdminUser } from './admin.config';
import { AuthService } from '../services/auth.service';

/**
 * Shared check: resolve the current user and test against the admin allowlist.
 * SSR-safe: on the server there is no authenticated user, so we allow the
 * navigation to proceed to the browser (where auth resolves) rather than
 * redirecting during server render. The real gate runs in the browser.
 */
async function passesAdminCheck(): Promise<boolean> {
  const auth = inject(AuthService);
  const platform = inject(PlatformService);
  const router = inject(Router);

  // On the server, don't hard-block — let it render and re-check on the client.
  if (platform.isServer) return true;

  const user = await firstValueFrom(
    auth.user$.pipe(
      filter((u): u is User | null => u !== undefined),
      take(1),
      timeout(4000),
      catchError(() => of(null)),
    ),
  );

  if (user && isAdminUser(user.email, user.uid)) return true;

  // Not an admin (or not logged in) — bounce home.
  router.navigateByUrl('/');
  return false;
}

export const adminCanActivate: CanActivateFn = () => passesAdminCheck();
export const adminCanMatch: CanMatchFn = () => passesAdminCheck();